import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Trash2 } from "lucide-react";
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

const CreateVehicleModel: React.FC = () => {
  const [modelName, setModelName] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [yearMake, setYearMake] = useState<number | "">("");
  const [buildings, setBuildings] = useState<{ id: number; building_name: string }[]>([]);
  const [workStations, setWorkStations] = useState<{ id: number; work_station: string }[]>([]);
  const [workInstructions, setWorkInstructions] = useState([
    { id: Date.now(), buildingId: "", workStationId: "", title: "", description: "", file: null }
  ]);
  const [vehicleModelId, setVehicleModelId] = useState<number | null>(null);
  const [isModelSubmitted, setIsModelSubmitted] = useState(false);
  const navigate = useNavigate();

  // Fetch buildings from API
  useEffect(() => {
    fetch("http://172.16.101.250:4500/api/buildings")
      .then((res) => res.json())
      .then((data) => setBuildings(data))
      .catch((err) => console.error("Error fetching buildings:", err));
  }, []);

  // Fetch workstations when a building is selected
  const fetchWorkStations = async (buildingId: string, index: number) => {
    if (!buildingId) return;
    try {
      const response = await fetch(`http://172.16.101.250:4500/api/buildings/${buildingId}/work_stations`);
      const data = await response.json();
      setWorkStations(data);

      // Reset workstation selection when changing buildings
      setWorkInstructions((prev) =>
        prev.map((wi, i) => (i === index ? { ...wi, workStationId: "" } : wi))
      );
    } catch (error) {
      console.error("Error fetching workstations:", error);
    }
  };

  // Add a new work instruction section
  const handleAddWorkInstruction = () => {
    setWorkInstructions([
      ...workInstructions,
      { id: Date.now(), buildingId: "", workStationId: "", title: "", description: "", file: null }
    ]);
  };

  // Remove a work instruction section
  const handleRemoveWorkInstruction = (id: number) => {
    setWorkInstructions(workInstructions.filter((wi) => wi.id !== id));
  };

  // Upload file and get path
  const handleFileUpload = async (file: File, index: number) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch("http://172.16.101.250:4500/api/upload_work_instruction", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const filePath = await response.text();
      setWorkInstructions((prev) =>
        prev.map((wi, i) => (i === index ? { ...wi, file: filePath } : wi))
      );
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // Submit vehicle model
  const handleSubmitVehicleModel = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      model_name: modelName,
      vehicle_type: vehicleType,
      year_make: yearMake === "" ? 0 : Number(yearMake),
    };

    try {
      const response = await fetch("http://172.16.101.250:4500/api/vehicle_model", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to create vehicle model");

      const data = await response.json();
      setVehicleModelId(data.id);
      setIsModelSubmitted(true); // Disable button & show "Model Submitted"
    } catch (error) {
      console.error("Error creating vehicle model:", error);
    }
  };

  // Submit work instructions
  const handleSubmitWorkInstructions = async () => {
    if (!vehicleModelId) return;

    try {
      await Promise.all(
        workInstructions.map(async (wi) => {
          const payload = {
            author: "Admin", // Change accordingly
            author_email: "admin@example.com", // Change accordingly
            title: wi.title,
            description: wi.description,
            file: wi.file || "",
            vehicle_model_id: vehicleModelId, // ✅ Include vehicle model ID
            work_station_id: wi.workStationId ? Number(wi.workStationId) : 0, // ✅ Include workstation ID
          };

          const response = await fetch("http://172.16.101.250:4500/api/work_instruction", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

          if (!response.ok) throw new Error("Failed to submit work instruction");
        })
      );

      console.log("All work instructions submitted successfully!");
      navigate("/vehicle-dashboard");
    } catch (error) {
      console.error("Error submitting work instructions:", error);
    }
  };

  return (
    <div className="min-h-screen h-screen bg-blue-900">
      {/* Header */}
      <div className="p-4">
        <img src={digitalOceanImageUrl("new%20logos/Asset%204.png")} alt="KMC Logo" className="w-16 h-auto ml-auto h-24 w-27" />
      </div>
      <div className="p-6 flex justify-between items-center">
        <h1 className="text-white text-lg font-medium">Create Vehicle Model</h1>
      </div>

      <div className="bg-white rounded-3xl p-6">
        <form onSubmit={handleSubmitVehicleModel}>
          <h2 className="text-blue-900 text-xl font-bold mb-4">Vehicle Model Information</h2>
          <input type="text" value={modelName} onChange={(e) => setModelName(e.target.value)} placeholder="Model Name" className="w-full p-2 border bg-sky-200 rounded mb-4" required />
          <input type="text" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} placeholder="Vehicle Type" className="w-full p-2 border bg-sky-200 rounded mb-4" required />
          <input type="number" value={yearMake} onChange={(e) => setYearMake(e.target.value === "" ? "" : Number(e.target.value))} placeholder="Year Make" className="w-full p-2 border bg-sky-200 rounded mb-4" />
          <button type="submit" disabled={isModelSubmitted} className={`w-full text-white px-3 py-3 rounded ${isModelSubmitted ? "bg-gray-500" : "bg-blue-900 hover:bg-blue-800"} `}>
            {isModelSubmitted ? "Model Submitted" : "Save Model"}
          </button>
        </form>

        <h2 className="text-blue-900 text-xl font-bold mt-6">Work Instructions</h2>
        {workInstructions.map((wi, index) => (
          <div key={wi.id} className="border p-4 rounded-lg mt-4 bg-gray-100">
            <select className="w-full p-2 border bg-white rounded mb-2" onChange={(e) => { fetchWorkStations(e.target.value, index); setWorkInstructions(prev => prev.map((w, i) => i === index ? { ...w, buildingId: e.target.value } : w)); }}>
              <option value="">Select Building</option>
              {buildings.map((b) => (<option key={b.id} value={b.id}>{b.building_name}</option>))}
            </select>
            <select className="w-full p-2 border bg-white rounded mb-2" onChange={(e) => setWorkInstructions(prev => prev.map((w, i) => i === index ? { ...w, workStationId: e.target.value } : w))}>
              <option value="">Select Work Station</option>
              {workStations.map((ws) => (<option key={ws.id} value={ws.id}>{ws.work_station}</option>))}
            </select>
            <input type="file" onChange={(e) => handleFileUpload(e.target.files![0], index)} />
          </div>
        ))}
        <button onClick={handleSubmitWorkInstructions} className="w-full bg-blue-900 text-white px-3 py-3 rounded mt-4">Submit Work Instructions</button>
      </div>
    </div>
  );
};

export default CreateVehicleModel;
